import React, { useState } from "react";
import { Button, Tag, Space } from "antd";
import { CheckCheck } from "lucide-react";
import DataTable from "../../components/DataTable";
import {
  useGetNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from "../../../hooks/api/notification/hooks";
import { INotificationItem } from "../../../hooks/api/notification/callbacks";
import { useMessageApi } from "../../../provider/AntdNotification";
import type { ColumnsType } from "antd/es/table";

const NotificationList: React.FC = () => {
  const messageApi = useMessageApi();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: notificationsData, isLoading, isError } = useGetNotifications();
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  const notifications: INotificationItem[] = notificationsData?.data || [];
  const unreadCount = notifications.filter((item) => !item.read).length;

  const handleMarkAll = () => {
    markAllAsReadMutation.mutate(undefined as any, {
      onSuccess: () => messageApi.success("All notifications marked as read"),
    });
  };

  const handleMarkSingle = (id: string) => {
    markAsReadMutation.mutate(id, {
      onSuccess: () => messageApi.success("Notification marked as read"),
    });
  };

  const columns: ColumnsType<INotificationItem> = [
    {
      title: "Notification Message",
      key: "message",
      render: (_, record) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800 text-sm">
              {record.title}
            </span>
            {!record.read && (
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
            )}
          </div>
          <p className="text-xs text-slate-600 leading-normal">{record.message}</p>
        </div>
      ),
    },
    {
      title: "Event Type",
      dataIndex: "type",
      key: "type",
      width: 180,
      render: (type: string) => (
        <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded border border-slate-200">
          {type}
        </span>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (date: string) => (
        <span className="text-xs text-slate-500">
          {date ? new Date(date).toLocaleString() : "-"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "read",
      key: "read",
      width: 110,
      render: (read: boolean) => (
        <Tag color={read ? "default" : "blue"}>
          {read ? "Read" : "Unread"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      align: "right",
      render: (_, record) =>
        !record.read ? (
          <Button
            type="link"
            size="small"
            onClick={() => handleMarkSingle(record._id)}
            loading={markAsReadMutation.isPending}
          >
            Mark Read
          </Button>
        ) : (
          <span className="text-xs text-slate-400">Read</span>
        ),
    },
  ];

  const paginatedData = notifications.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  return (
    <div className="w-full">
      <DataTable<INotificationItem>
        title={
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-800">Notifications</h1>
            {unreadCount > 0 && (
              <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-blue-200">
                {unreadCount} Unread
              </span>
            )}
          </div>
        }
        headerActions={
          unreadCount > 0 ? (
            <Button
              type="default"
              icon={<CheckCheck size={16} />}
              onClick={handleMarkAll}
              loading={markAllAsReadMutation.isPending}
            >
              Mark All as Read
            </Button>
          ) : null
        }
        dataSource={paginatedData}
        columns={columns}
        loading={isLoading}
        rowKey="_id"
        isError={isError}
        paginationProps={{
          page,
          limit: pageSize,
          total: notifications.length,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default NotificationList;
